import { ChangeDetectionStrategy, Component, OnDestroy, OnInit } from '@angular/core';
import { cloneDeep, isArray } from 'lodash-es';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { filter, map, takeUntil, tap } from 'rxjs/operators';

import {
  Feature,
  FeatureActionFields,
  FeatureCommands,
  FeatureNames,
  FeaturePropertyKey,
  ScheduleCommands,
  ScheduleProperties,
  VentilationProgramDetails,
} from '@vi-iot/client';

import { FeatureService, InstallationFeaturesStore } from '@store';

import {
  Program,
  ScheduleEntry,
  ScheduleService,
  TimemanagerDialogService,
} from '../../../../../features/timemanager/services';
import { CustomTranslationsService } from '../../../../../operational-data-common/translations';
import { SUFFIX } from '../../../../../shared/common/units';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'app-ventilation-panel',
  templateUrl: './ventilation-panel.component.html',
  styleUrls: ['./ventilation-panel.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class VentilationPanelComponent implements OnInit, OnDestroy {
  ventilationSchedule$: Observable<Feature<ScheduleProperties, ScheduleCommands>>;

  editMultipleDays: boolean;
  programs$: BehaviorSubject<Program[]> = new BehaviorSubject<Program[]>([]);
  programsDetails: VentilationProgramDetails[];

  ngUnsubscribe: Subject<void> = new Subject<void>();

  SUFFIX = SUFFIX;

  constructor(
    private featureService: FeatureService,
    private scheduleService: ScheduleService,
    private timemanagerDialogService: TimemanagerDialogService,
    private customTranslationsService: CustomTranslationsService,
    private installationFeaturesStore: InstallationFeaturesStore,
    private translateService: TranslateService
  ) {}

  ngOnInit(): void {
    this.ventilationSchedule$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.ventilationSchedule
    );
    this.programs$ = this.scheduleService.programs$;
    this.installationFeaturesStore
      .getVentilationProgramsDetails()
      .pipe(
        filter((details) => !!details && isArray(details)),
        takeUntil(this.ngUnsubscribe)
      )
      .subscribe((details) => {
        this.programsDetails = details
          .map<VentilationProgramDetails>((programWithDetails) => ({
            ...programWithDetails,
            displayName: this.translateService.instant(
              this.customTranslationsService.VENTILATION_PROGRAMS[programWithDetails.name]
            ),
          }))
          .sort((a, b) => a.displayName.localeCompare(b.displayName));
      });
    this.initSchedule();
  }

  ngOnDestroy(): void {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  initSchedule(): void {
    this.editMultipleDays = true;

    this.ventilationSchedule$
      .pipe(
        takeUntil(this.ngUnsubscribe),
        filter((feature) => !!feature?.properties?.entries.value),
        tap((feature) => this.scheduleService.createSchedule(feature)),
        map((feature) => this.featureService.getScheduleModes(feature)),
        tap((modes) => this.scheduleService.createModes(modes))
      )
      .subscribe();
  }

  onEditMultipleDays(): void {
    this.timemanagerDialogService.openSelectDaysDialog();
  }

  onScheduleChange(
    event: { scheduleIds: number[]; periods: ScheduleEntry[] },
    feature: Feature<ScheduleProperties, ScheduleCommands>
  ): void {
    const schedule = this.scheduleService.getScheduleEntries(feature);
    const value = this.scheduleService.getScheduleValue(event.scheduleIds, event.periods, schedule);
    this.installationFeaturesStore.updateFeature(
      FeatureNames.ventilationSchedule,
      cloneDeep(value),
      FeaturePropertyKey.entries,
      FeatureActionFields.newSchedule,
      FeatureCommands.setSchedule
    );
  }
}
